<template>
  <div class="row">
    <div class="col-md-12">
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-5">
        <b-spinner class="align-middle"></b-spinner>
        <span class="ml-2">Loading offer...</span>
      </div>

      <!-- Content -->
      <template v-else>
        <!-- Header Card -->
        <div class="card mb-3">
          <div class="card-body">
            <div
              class="d-flex flex-wrap justify-content-between align-items-start"
            >
              <div class="mr-3 mb-2" style="min-width: 0">
                <h4 class="mb-1 text-break">{{ offerTitle }}</h4>
                <div class="text-muted mb-2">
                  <code class="mr-2">ID: {{ offer.id }}</code>
                  <code v-if="offer.internal_reference_number">Ref: {{ offer.internal_reference_number }}</code>
                </div>
                <div>
                  <b-badge v-if="offer.feed_in" variant="info" class="mr-1">{{ offer.feed_in.legacy_client }}</b-badge>
                  <b-badge v-if="offer.is_agency" variant="warning" class="mr-1">Agency</b-badge>
                  <b-badge :variant="statusVariant" class="mr-1">{{ offer.status }}</b-badge>
                  <b-badge v-if="offer.featured" variant="warning" class="mr-1"
                    >Featured</b-badge
                  >
                </div>
                <div v-if="offer.featured" class="text-muted small mt-1">
                  {{ featuredSourceLabel }} ·
                  {{ formatDate(offer.featured.featured_at) }}
                </div>
              </div>
              <div class="d-flex flex-wrap align-items-start">
                <b-button
                  :variant="offer.featured ? 'outline-dark' : 'dark'"
                  class="mr-2 mb-2"
                  :aria-busy="featuredPending ? 'true' : 'false'"
                  @click="toggleFeatured"
                >
                  <b-spinner
                    v-if="featuredPending"
                    small
                    class="mr-1"
                  ></b-spinner>
                  {{ offer.featured ? "Remove Featured" : "Feature Offer" }}
                </b-button>
                <router-link
                  :to="{ name: 'offers' }"
                  class="btn btn-outline-secondary mb-2"
                >
                  <i class="fas fa-arrow-left mr-1"></i> Back to Offers
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Recruiter contact email (ES-1733): where applications are forwarded -->
        <div class="card mb-3">
          <div class="card-body">
            <form
              novalidate
              @submit.prevent="saveRecruiterEmail(recruiterEmail)"
            >
              <label
                for="recruiter-contact-email"
                class="font-weight-bold mb-1"
              >
                Recruiter contact email
              </label>
              <div class="d-flex flex-wrap align-items-start">
                <input
                  id="recruiter-contact-email"
                  v-model="recruiterEmail"
                  type="email"
                  inputmode="email"
                  autocomplete="off"
                  spellcheck="false"
                  class="form-control mr-2 mb-2"
                  style="max-width: 24rem"
                  aria-describedby="recruiter-contact-email-help"
                />
                <b-button
                  type="submit"
                  variant="dark"
                  class="mr-2 mb-2"
                  :disabled="recruiterEmailPending"
                  :aria-busy="recruiterEmailPending ? 'true' : 'false'"
                  >Save</b-button
                >
                <b-button
                  v-if="offer.recruiter_contact_email_overridden"
                  variant="outline-secondary"
                  class="mb-2"
                  :disabled="recruiterEmailPending"
                  @click="saveRecruiterEmail(null)"
                  >Reset to feed</b-button
                >
              </div>
              <small
                id="recruiter-contact-email-help"
                class="form-text text-muted"
              >
                {{
                  offer.recruiter_contact_email_overridden
                    ? "Set by hand: feed imports keep this address."
                    : "From the feed. Saving an address keeps it across imports; an empty value stops forwarding until the next import."
                }}
              </small>
            </form>
          </div>
        </div>

        <!-- Tabs -->
        <b-tabs content-class="mt-3" nav-class="mb-0">
          <b-tab title="Details" active>
            <OfferTabDetails
              :offer="offer"
              :offerId="offerId"
              @offer-updated="fetch"
            />
          </b-tab>
          <b-tab title="Publishing">
            <OfferTabPublishing
              :offer="offer"
              :offerId="offerId"
              @diffusion-updated="fetch"
            />
          </b-tab>
          <b-tab title="AI & Classification">
            <OfferTabAI
              :offer="offer"
              :offerId="offerId"
              @offer-updated="fetch"
            />
          </b-tab>
        </b-tabs>
      </template>
    </div>
  </div>
</template>

<script>
import { BSpinner, BBadge, BButton, BTabs, BTab } from "bootstrap-vue";
import OfferTabDetails from "@/components/Offers/OfferTabDetails.vue";
import OfferTabPublishing from "@/components/Offers/OfferTabPublishing.vue";
import OfferTabAI from "@/components/Offers/OfferTabAI.vue";

export default {
  components: {
    BSpinner,
    BBadge,
    BButton,
    BTabs,
    BTab,
    OfferTabDetails,
    OfferTabPublishing,
    OfferTabAI
  },
  props: {
    offerId: {
      required: true,
      type: [String, Number]
    }
  },
  data() {
    return {
      offer: {},
      isLoading: true,
      featuredPending: false,
      recruiterEmail: "",
      recruiterEmailPending: false
    };
  },
  async created() {
    await this.fetch();
    this.isLoading = false;
  },
  computed: {
    offerTitle() {
      if (!this.offer.contents) return "";
      const lang = this.offer.primary_language || "fr";
      return this.offer.contents[lang]?.title || Object.values(this.offer.contents)[0]?.title || "";
    },
    statusVariant() {
      const variants = { published: "success", draft: "secondary", expired: "danger", pending: "warning" };
      return variants[this.offer.status] || "secondary";
    },
    featuredSourceLabel() {
      return this.offer.featured.source === "slot"
        ? "Client slot"
        : "Granted by an admin";
    }
  },
  methods: {
    async fetch() {
      const { data } = await this.$http.get(`/enroll_admin/offer/${this.offerId}`);
      this.offer = data;
      this.recruiterEmail = data.recruiter_contact_email || "";
    },
    // null or blank = reset: the next feed import restores the feed address.
    async saveRecruiterEmail(value) {
      if (this.recruiterEmailPending) return;
      this.recruiterEmailPending = true;
      try {
        await this.$http.put(
          `/enroll_admin/offer/${this.offerId}/recruiter-contact-email`,
          {
            recruiter_contact_email:
              value === null ? null : value.trim() || null
          }
        );
        await this.fetch();
        this.$bvToast.toast("Recruiter contact email saved", {
          title: "Recruiter contact email",
          variant: "success",
          solid: true
        });
      } catch (e) {
        // 422 = { recruiter_contact_email: [message] }
        const errors =
          e.response && e.response.status === 422 && e.response.data;
        const message =
          errors &&
          errors.recruiter_contact_email &&
          errors.recruiter_contact_email[0];
        this.$bvToast.toast(
          message || "Could not save the recruiter contact email",
          {
            title: "Recruiter contact email",
            variant: "danger",
            solid: true
          }
        );
      } finally {
        this.recruiterEmailPending = false;
      }
    },
    formatDate(dateStr) {
      return dateStr ? new Date(dateStr).toLocaleDateString() : "";
    },
    async toggleFeatured() {
      if (this.featuredPending) return;
      // A slot-sourced feature belongs to the client: confirm before freeing it.
      if (this.offer.featured && this.offer.featured.source === "slot") {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          "This offer uses one of the client’s featured slots. Removing it frees the slot and resets its featured date.",
          {
            title: "Remove Featured",
            okTitle: "Remove Featured",
            okVariant: "danger",
            cancelTitle: "Cancel"
          }
        );
        if (!confirmed) return;
      }
      this.featuredPending = true;
      const url = `/enroll_admin/offer/${this.offerId}/featured`;
      try {
        if (this.offer.featured) await this.$http.delete(url);
        else await this.$http.post(url);
        await this.fetch();
      } catch (e) {
        // 422 carries a readable `message` (offer_not_open, offer_not_published_internal)
        const message =
          e.response &&
          e.response.status === 422 &&
          e.response.data &&
          e.response.data.message;
        this.$bvToast.toast(message || "Could not update the featured state", {
          title: "Featured",
          variant: "danger",
          solid: true
        });
      } finally {
        this.featuredPending = false;
      }
    }
  }
};
</script>
